import { Badge, Button, Flex, Tooltip } from "antd";
import { useEffect, useState, useRef } from "react";
import { Message } from "./Message";
import { useChatbot, type ChatMessage } from "@libertify.frontend/chatbot";
import ScrollToBottom from "react-scroll-to-bottom";
import styles from "./Chat.module.css";
import { PromptForm } from "../PromptHistory/PromptForm";
import { CHATBOT_WS_URL, RECONNECT_BACKOFF_COUNT } from "../../../../constants";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../hooks/useAuth";
import { useTranslate } from "@refinedev/core";

export const Chat = ({
  documentIds,
  organizationId,
  showEmptyMessages = true,
  showDot = true,
}: {
  documentIds: string[];
  organizationId: string;
  showEmptyMessages?: boolean;
  showDot?: boolean;
}) => {
  const t = useTranslate();
  const { accessToken: bearerToken } = useAuth();
  const socketUrl = `${CHATBOT_WS_URL}`;
  const [reconnectCount, setReconnectCount] = useState(0);
  const [showMessages, setShowMessages] = useState(showEmptyMessages);
  const sessionIdRef = useRef<string | undefined>(undefined);
  const {
    sendQuery,
    messageHistory,
    isConnected,
    isBusy,
    sendJsonMessage,
    connect,
    connectionStatus,
  } = useChatbot(socketUrl, {
    shouldReconnect: (_closeEvent) => {
      if (reconnectCount < RECONNECT_BACKOFF_COUNT) {
        setReconnectCount((prev) => prev + 1);
        return true;
      }
      return false;
    },
    onOpen: () => {
      const data = {
        action: "OpenSocket",
        context: {
          organization_id: "libertify",
          session_id: sessionIdRef.current,
          organization_context: {
            organization_id: organizationId,
            document_ids: documentIds,
            bearer_token: bearerToken,
            password: bearerToken,
          },
        },
      };
      sendJsonMessage(data);
    },
    onMessage: (data) => {
      const d = data as Record<string, unknown>;
      if (d?.session_id) {
        sessionIdRef.current = d.session_id as string;
      }
    },
    onClose: (event) => console.log("❌ WebSocket disconnected", event),
    onError: (event) => console.error("❌ Error:", event),
  });

  const handleChange = (value: string) => {
    sendQuery(value);
    setShowMessages(true);
  };

  useEffect(() => {
    if (bearerToken) {
      connect();
    }
  }, [bearerToken, connect]);

  const loading = false; // messageHistory.length === 0;
  if (loading) return <LoadingOutlined spin style={{ fontSize: 24 }} />;

  return (
    <div
      style={{
        position: "relative",
        height: showMessages ? "calc(100vh - 30px - 50px)" : "auto",
      }}
    >
      {showMessages && (
        <ScrollToBottom
          followButtonClassName="scrollToBottomButton"
          className={styles.root}
        >
          <Flex vertical gap={40}>
            {messageHistory.map((message: ChatMessage) => (
              <Message
                key={message.queryId}
                message={message}
                sendQuery={sendQuery}
              />
            ))}
          </Flex>
        </ScrollToBottom>
      )}
      {!showEmptyMessages && showMessages && (
        <Button
          onClick={() => setShowMessages(false)}
          style={{ position: "absolute", top: 0, right: 20 }}
          icon={<CloseOutlined />}
        />
      )}

      <div
        style={{
          position: showMessages ? "absolute" : "relative",
          bottom: 0,
          width: "100%",
        }}
      >
        <PromptForm
          disabled={isBusy || !isConnected}
          onChange={handleChange}
          value=""
          placeholder={t(
            "components.PromptHistory.PromptForm.askYourQueryExperience"
          )}
        />
      </div>
      {showDot && (
        <Tooltip title={connectionStatus}>
          <Badge
            dot
            color={isConnected ? "green" : "red"}
            style={{ position: "absolute", bottom: 70, left: 0 }}
          />
        </Tooltip>
      )}
    </div>
  );
};
